// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useRef, useState, useCallback } from 'react'
import { useAuth } from '../auth/AuthContext'
import { authActivity } from '../api/client'

const IDLE_LIMIT_MS = 15 * 60 * 1000   // 15 min of no USER input → auto-logout
const WARN_BEFORE_MS = 60 * 1000       // show a 60s warning before logging out
const HEARTBEAT_MS = 60 * 1000         // at most one server activity ping per minute
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'] as const

/**
 * Idle auto-logout for workstation security (HIPAA automatic logoff §164.312(a)(2)(iii) /
 * workstation use §164.310(b)). After 15 minutes with no real user interaction the session is
 * signed out so PHI is not left exposed on an unattended terminal.
 *
 * Two enforcement layers:
 *  - client: this hook signs out at the 15-min mark (with a 60s "still there?" warning);
 *  - server: real interaction fires a throttled POST /auth/activity heartbeat, which is the ONLY
 *    thing that advances the session's idle clock — background polling no longer keeps it alive,
 *    so the backend independently idles the session out even if this hook never runs.
 *
 * NOTE: `warning` is held in a ref (not an effect dependency) on purpose. A previous version listed
 * `warning` in the effect deps, so when the warning fired the effect re-ran, called reset(), cleared
 * the warning, and restarted the 15-min timer — the session never actually logged out.
 */
export function useIdleLogout() {
  const { me, signOut } = useAuth()
  const authed = !!me?.authenticated
  const [warning, setWarning] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(0)
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const countdown = useRef<ReturnType<typeof setInterval> | null>(null)
  const warningRef = useRef(false)   // read inside the activity handler without re-subscribing
  const lastBeat = useRef(0)         // throttle the server heartbeat

  const setWarn = useCallback((v: boolean) => { warningRef.current = v; setWarning(v) }, [])

  const clearTimers = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current)
    if (warnTimer.current) clearTimeout(warnTimer.current)
    if (countdown.current) clearInterval(countdown.current)
  }, [])

  const reset = useCallback(() => {
    clearTimers()
    setWarn(false)
    if (!authed) return
    // Schedule the warning, then logout WARN_BEFORE_MS after that.
    warnTimer.current = setTimeout(() => {
      setWarn(true)
      setSecondsLeft(Math.floor(WARN_BEFORE_MS / 1000))
      countdown.current = setInterval(() => setSecondsLeft(s => Math.max(0, s - 1)), 1000)
    }, IDLE_LIMIT_MS - WARN_BEFORE_MS)
    idleTimer.current = setTimeout(() => { void signOut() }, IDLE_LIMIT_MS)
  }, [authed, clearTimers, setWarn, signOut])

  useEffect(() => {
    if (!authed) { clearTimers(); setWarn(false); return }
    reset()
    const onActivity = () => {
      // Throttled server heartbeat so the backend idle clock tracks USER activity, not polling.
      const now = Date.now()
      if (now - lastBeat.current > HEARTBEAT_MS) { lastBeat.current = now; void authActivity() }
      // While the warning is up, input must NOT silently reset — the user has to click "Stay".
      if (!warningRef.current) reset()
    }
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }))
    return () => {
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity))
      clearTimers()
    }
  }, [authed, reset, clearTimers, setWarn])

  const stayActive = useCallback(() => { lastBeat.current = 0; void authActivity(); reset() }, [reset])
  const logoutNow = useCallback(() => { void signOut() }, [signOut])

  return { warning: authed && warning, secondsLeft, stayActive, logoutNow }
}
